import { cn } from "@-/com.npmjs.cn";
import { cva, type VariantProps } from "@-/com.npmjs.cva";
import type { ComponentProps } from "react";

const websiteButtonVariants = cva({
  base: "inline-flex shrink-0 cursor-pointer items-center justify-center gap-2 rounded-full border border-transparent font-semibold whitespace-nowrap no-underline transition-[transform,color,background-color,border-color] duration-200 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-website-accent disabled:pointer-events-none disabled:opacity-50",
  compoundVariants: [{ class: "px-3", size: "md", variant: "ghost" }],
  defaultVariants: {
    size: "md",
    variant: "primary",
  },
  variants: {
    size: {
      lg: "min-h-12 px-6 text-base",
      md: "min-h-10 px-4.5 text-sm",
    },
    variant: {
      /** The brand orange, for the one download action that must stand out from its surroundings. */
      accent: "bg-website-accent font-bold text-black hover:-translate-y-0.5",
      /** Quiet text actions such as "Log in"; inherits the surrounding text color. */
      ghost: "font-medium text-current/55 hover:text-current",
      /** The default call to action. */
      primary: "bg-website-ink text-white hover:-translate-y-0.5",
      secondary: "border-black/15 bg-white text-black hover:-translate-y-0.5 hover:border-black/30",
    },
  },
});

export type WebsiteButtonVariantProps = VariantProps<typeof websiteButtonVariants>;

/** A pill-shaped link styled as a button. */
export function WebsiteButtonLink({
  className,
  size,
  variant,
  ...props
}: ComponentProps<"a"> & WebsiteButtonVariantProps) {
  return <a className={cn(websiteButtonVariants({ size, variant }), className)} {...props} />;
}

/** A pill-shaped `<button>`, for in-page actions and form submission. */
export function WebsiteButton({
  className,
  size,
  type = "button",
  variant,
  ...props
}: Omit<ComponentProps<"button">, "type"> &
  WebsiteButtonVariantProps & { type?: "button" | "submit" }) {
  return (
    <button
      className={cn(websiteButtonVariants({ size, variant }), className)}
      type={type === "submit" ? "submit" : "button"}
      {...props}
    />
  );
}
